import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCw, Smartphone, ScanLine, AudioLines, ShieldCheck, Gauge, Wrench, Landmark, ThumbsUp, ArrowRight } from 'lucide-react';
import RecordsBeat from './RecordsBeat';
import OBDBeat from './OBDBeat';

/**
 * Inspection3D — a full-frame, auto-playing "inspection film".
 *
 * One ref-guarded rAF clock drives a 14-beat edit. Beats are either the
 * interactive 36-frame Hyundai Creta 360° turntable (Vision-AI body scan +
 * accident / dent detection with animated bounding boxes) or photoreal studio
 * stills (headlights → transmission). The engine beat runs a Sound-AI
 * waveform. Everything is shot "through a phone" (mobile-capture HUD) and the
 * running repair estimate lives inside the frame, ending on a clear verdict.
 */

const FRAME_COUNT = 36;
const FRAME_URL = (i) => `/creta360/frame_${((i % FRAME_COUNT) + FRAME_COUNT) % FRAME_COUNT}.jpg`;
const CROSS = 0.45;

const SEV = {
  ok: { c: '#10b981', t: 'PASS' },
  attention: { c: '#f59e0b', t: 'ATTENTION' },
  major: { c: '#ef4444', t: 'CRITICAL' },
};

const inr = (n) => `₹ ${Math.round(n).toLocaleString('en-IN')}`;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

// ── The edit ──────────────────────────────────────────────────────────────
const RAW = [
  {
    id: 'mobile', type: 'image', src: '/inspection/mobile.jpg', dur: 4,
    ai: 'MOBILE', kicker: '01', label: 'It starts on a phone',
    sub: '200+ checkpoints captured on the Wisedrive app — at the seller\u2019s doorstep',
    severity: 'ok', cost: 0, metric: { label: 'Checkpoints', from: 0, to: 200, unit: '' },
  },
  {
    id: 'exterior', type: 'turntable', spin: true, dur: 5,
    ai: 'VISION', kicker: '02', label: 'Exterior 360° · Vision AI',
    sub: 'Every panel photographed and analysed for paint, alignment & damage',
    severity: 'ok', cost: 0, metric: { label: 'Body scan', from: 0, to: 100, unit: '%' },
  },
  {
    id: 'accident', type: 'image', src: '/inspection/vision_gap.jpg', dur: 5, vision: true,
    ai: 'VISION', kicker: '03', label: 'Panel gaps · Accident scan',
    sub: 'Vision AI zooms into every shut-line, measures the panel gap and flags a repaired front-left impact',
    severity: 'major', cost: 8400, lineItem: 'Front-left panel + paint repair',
    gap: { value: '6.1 mm', status: 'MISALIGNED', x: 50, y: 46 },
    boxes: [{ x: 34, y: 16, w: 30, h: 66, label: 'ACCIDENT · repaired panel', conf: 94, sev: 'major' }],
  },
  {
    id: 'dents', type: 'image', src: '/inspection/vision_dentscratch.jpg', dur: 5, vision: true,
    ai: 'VISION', kicker: '04', label: 'Dents & scratches',
    sub: 'Vision AI zooms into the door skin and maps every dent and clear-coat scratch',
    severity: 'attention', cost: 3200, lineItem: 'Panel polish + clear-coat',
    boxes: [
      { x: 16, y: 16, w: 40, h: 40, label: 'DENT · 4 cm', conf: 88, sev: 'attention' },
      { x: 26, y: 58, w: 54, h: 15, label: 'SCRATCH · 12 cm', conf: 82, sev: 'attention' },
    ],
  },
  {
    id: 'headlights', type: 'image', src: '/inspection/headlights.jpg', src2: '/inspection/headlights_rear.jpg', dur: 6,
    ai: 'SENSOR', kicker: '05', label: 'Headlights & Lights',
    sub: 'Front LED · DRL · rear tail & brake · beam alignment', severity: 'ok', cost: 0,
    checks: [
      { label: 'Low beam', value: 'Aligned', sev: 'ok' },
      { label: 'High beam', value: 'Aligned', sev: 'ok' },
      { label: 'DRL / LED', value: 'All working', sev: 'ok' },
      { label: 'Tail & brake', value: 'All working', sev: 'ok' },
      { label: 'Lens clarity', value: 'Clear', sev: 'ok' },
      { label: 'Beam aim', value: 'In spec', sev: 'ok' },
    ],
  },
  {
    id: 'engine', type: 'image', src: '/inspection/engine.jpg', dur: 5,
    ai: 'SOUND', kicker: '06', label: 'Engine · Sound AI',
    sub: 'A 5-second idle clip is analysed for knock, misfire & chain rattle',
    severity: 'attention', cost: 15000, lineItem: 'Timing chain inspection & fix',
    metric: { label: 'Idle', text: 'IRREGULAR' },
  },
  {
    id: 'battery', type: 'image', src: '/inspection/battery.jpg', dur: 4.2,
    ai: 'SENSOR', kicker: '07', label: 'Battery',
    sub: 'Rest voltage · cranking · state-of-health · terminals · age', severity: 'attention', cost: 5500,
    lineItem: 'Battery replacement (ageing)',
    note: '≈ 6 months of healthy life left — plan a change',
    checks: [
      { label: 'Rest voltage', value: '12.2 V', sev: 'attention', improve: true },
      { label: 'Cranking', value: '9.4 V', sev: 'ok' },
      { label: 'Health (SOH)', value: '68%', sev: 'attention', improve: true },
      { label: 'Age', value: '4.5 yrs', sev: 'attention', improve: true },
      { label: 'Terminals', value: 'Clean', sev: 'ok' },
    ],
  },
  {
    id: 'obd', type: 'obd', dur: 10.5,
    ai: 'OBD', kicker: '08', label: 'OBD-II Diagnostic', severity: 'ok', cost: 0,
  },
  {
    id: 'ac', type: 'image', src: '/inspection/ac.jpg', dur: 4,
    ai: 'SENSOR', kicker: '08', label: 'Air Conditioning',
    sub: 'Vent temperature · compressor · gas charge · cabin filter', severity: 'ok', cost: 0,
    checks: [
      { label: 'Vent temp', value: '8 °C', sev: 'ok' },
      { label: 'Compressor', value: 'Smooth', sev: 'ok' },
      { label: 'Gas charge', value: '95%', sev: 'ok' },
      { label: 'Cool-down', value: '42 s', sev: 'ok' },
      { label: 'Cabin filter', value: 'Dusty', sev: 'attention', improve: true },
    ],
  },
  {
    id: 'interior', type: 'image', src: '/inspection/interior.jpg', dur: 4,
    ai: 'SENSOR', kicker: '09', label: 'Interior',
    sub: 'Seats · trim · infotainment · airbags · sunroof', severity: 'attention', cost: 2200,
    lineItem: 'Sunroof rail service',
    checks: [
      { label: 'Seats & trim', value: 'Minor wear', sev: 'attention', improve: true },
      { label: 'Infotainment', value: 'Working', sev: 'ok' },
      { label: 'Airbags', value: '6 / 6 OK', sev: 'ok' },
      { label: 'Sunroof', value: 'Rail service', sev: 'attention', improve: true },
      { label: 'Power windows', value: 'All OK', sev: 'ok' },
    ],
  },
  {
    id: 'brakes', type: 'image', src: '/inspection/brakes.jpg', dur: 4.5,
    ai: 'SENSOR', kicker: '10', label: 'Brakes',
    sub: 'Pads · rotor runout · caliper · brake-fluid moisture', severity: 'attention', cost: 6800,
    lineItem: 'Front brake pad set',
    boxes: [{ x: 30, y: 30, w: 42, h: 42, label: 'FRONT PADS · 3.2 mm', sev: 'attention' }],
    checks: [
      { label: 'Front pads', value: '3.2 mm', sev: 'attention', improve: true },
      { label: 'Rear pads', value: '5.8 mm', sev: 'ok' },
      { label: 'Rotor runout', value: '0.05 mm', sev: 'ok' },
      { label: 'Brake fluid', value: '3% moisture', sev: 'attention', improve: true },
      { label: 'Handbrake', value: 'Holds', sev: 'ok' },
    ],
  },
  {
    id: 'tyres', type: 'image', src: '/inspection/tyres.jpg', dur: 4,
    ai: 'SENSOR', kicker: '11', label: 'Tyres',
    sub: 'Digital tread gauge · 3 points per tyre', severity: 'major', cost: 12000,
    lineItem: 'Front-left tyre replacement', metric: { label: 'Tread', from: 8, to: 2.1, unit: ' mm', dp: 1 },
    note: '≈ 4,500 km left before it\u2019s unsafe — replace now',
  },
  {
    id: 'suspension', type: 'image', src: '/inspection/suspension.jpg', dur: 4.5,
    ai: 'SENSOR', kicker: '12', label: 'Suspension',
    sub: 'Strut · coil spring · bump stop · bushings · ball joints', severity: 'attention', cost: 4500,
    lineItem: 'Bump-stop + bushing replacement',
    boxes: [{ x: 34, y: 22, w: 26, h: 46, label: 'BUMP STOP · 15% wear', sev: 'attention' }],
    checks: [
      { label: 'Damper (FL)', value: '85%', sev: 'attention', improve: true },
      { label: 'Coil spring', value: 'OK', sev: 'ok' },
      { label: 'Bump stop', value: '15% wear', sev: 'attention', improve: true },
      { label: 'Bushings', value: 'Hairline', sev: 'attention', improve: true },
      { label: 'Ball joints', value: 'OK', sev: 'ok' },
    ],
  },
  {
    id: 'transmission', type: 'image', src: '/inspection/transmission_gear.jpg', dur: 5, gear: true,
    ai: 'SENSOR', kicker: '13', label: 'Transmission · Shift test',
    sub: 'Mechanic runs through every gear — Shift AI checks smoothness & gear jump', severity: 'attention', cost: 1800,
    lineItem: 'Gearbox oil + linkage service',
  },
  {
    id: 'testdrive', type: 'image', src: '/inspection/testdrive.jpg', dur: 5,
    ai: 'SENSOR', kicker: '14', label: 'Test drive',
    sub: 'Clutch bite · steering pull · brake feel · gear engagement · NVH', severity: 'attention', cost: 1200,
    lineItem: 'Wheel alignment',
    note: 'Drives tight — only a mild left pull to dial out',
    checks: [
      { label: 'Clutch bite', value: 'Healthy', sev: 'ok' },
      { label: 'Gear engagement', value: 'Smooth', sev: 'ok' },
      { label: 'Brake feel', value: 'Firm', sev: 'ok' },
      { label: 'Steering pull', value: 'Slight left', sev: 'attention', improve: true },
      { label: 'NVH / vibration', value: 'Low', sev: 'ok' },
    ],
  },
  {
    id: 'records', type: 'records', dur: 10,
    ai: 'RECORDS', kicker: '15', label: 'Records & Documents', severity: 'ok', cost: 0,
  },
  {
    id: 'verdict', type: 'verdict', dur: 8,
    ai: 'VERDICT', kicker: '16', label: 'Verdict', severity: 'attention',
    score: 6.8, cost: 0,
  },
];

let _acc = 0;
const CH = RAW.map((c, i) => { const start = _acc; _acc += c.dur; return { ...c, start, end: _acc, kicker: String(i + 1).padStart(2, '0') }; });
const TOTAL = _acc;
const BEAT_COUNT = CH.length;
const COST_ITEMS = CH.filter((c) => c.cost > 0);
const GRAND_TOTAL = COST_ITEMS.reduce((s, c) => s + c.cost, 0);

// ── Overlays ────────────────────────────────────────────────────────────────
const PhoneHud = () => (
  <div className="pointer-events-none absolute inset-0 z-20">
    <div className="absolute inset-6 border border-white/10 rounded-md" />
    {[33.33, 66.66].map((p) => (
      <React.Fragment key={p}>
        <div className="absolute top-6 bottom-6 w-px bg-white/10" style={{ left: `${p}%` }} />
        <div className="absolute left-6 right-6 h-px bg-white/10" style={{ top: `${p}%` }} />
      </React.Fragment>
    ))}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 border border-white/40 rounded" />
  </div>
);

const ScanSweep = ({ t, color }) => (
  <>
    <div className="pointer-events-none absolute inset-x-0 z-20" style={{ top: `${(t % 1) * 100}%`, height: '2px', background: `linear-gradient(90deg,transparent,${color},transparent)`, boxShadow: `0 0 16px 2px ${color}` }} />
    <div className="pointer-events-none absolute inset-0 z-10 opacity-[0.10]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)', backgroundSize: '46px 46px' }} />
  </>
);

const VisionBox = ({ box, appear }) => {
  const col = SEV[box.sev].c;
  return (
    <motion.div
      className="absolute z-30 pointer-events-none"
      style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%` }}
      initial={{ opacity: 0, scale: 1.15 }}
      animate={{ opacity: appear, scale: 1 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <div className="absolute inset-0 rounded-[3px]" style={{ border: `2px solid ${col}`, boxShadow: `0 0 20px ${col}66, inset 0 0 12px ${col}22` }} />
      {['-top-px -left-px', '-top-px -right-px', '-bottom-px -left-px', '-bottom-px -right-px'].map((pos) => (
        <span key={pos} className={`absolute ${pos} h-2.5 w-2.5`} style={{ borderColor: col, borderStyle: 'solid', borderWidth: pos.includes('top') ? '2px 0 0 0' : '0 0 2px 0', borderLeftWidth: pos.includes('left') ? 2 : 0, borderRightWidth: pos.includes('right') ? 2 : 0 }} />
      ))}
      <div className={`absolute ${box.y < 20 ? 'top-1' : '-top-6'} left-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[9px] font-black tracking-wider uppercase`} style={{ background: col, color: '#0b1220' }}>
        {box.label}{box.conf != null ? ` · ${box.conf}%` : ''}
      </div>
    </motion.div>
  );
};

const GAP_SEV = { ok: '#10b981', attention: '#f59e0b', major: '#ef4444' };

const CheckPanel = ({ title, items, t }) => {
  const improve = items.filter((i) => i.improve).length;
  return (
    <div data-testid="inspection-check-panel" className="absolute z-30 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 sm:left-auto sm:right-4 sm:translate-x-0 w-[86%] sm:w-72">
      <div className="rounded-xl border border-white/15 bg-slate-950/[0.98] backdrop-blur-md p-3 shadow-2xl shadow-black/60 ring-1 ring-white/5">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-[0.16em] uppercase text-white"><Gauge className="h-3.5 w-3.5 text-blue-300" /> {title}</span>
          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase text-blue-300"><span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" /> AI</span>
        </div>
        <ul className="space-y-1.5">
          {items.map((it, i) => {
            const rowT = clamp((t - 0.1 - i * 0.12) / 0.35, 0, 1);
            const col = GAP_SEV[it.sev];
            return (
              <li key={it.label} className="flex items-center gap-2 text-[11px]" style={{ opacity: 0.35 + 0.65 * clamp(rowT * 3, 0, 1) }}>
                <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: col }} />
                <span className="flex-1 min-w-0 truncate text-slate-50 font-medium">{it.label}</span>
                {it.improve && rowT > 0.6 && (
                  <span className="shrink-0 rounded px-1 py-px text-[7px] font-black tracking-wide uppercase" style={{ background: `${col}33`, color: col, border: `1px solid ${col}88` }}>improve</span>
                )}
                <span className="shrink-0 text-right font-mono font-bold tabular-nums" style={{ color: col, textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>{it.value}</span>
              </li>
            );
          })}
        </ul>
        <div className="mt-2.5 border-t border-white/10 pt-2 text-center text-[10px] font-bold tracking-wide uppercase" style={{ color: improve ? '#fbbf24' : '#34d399', textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
          {improve ? `${improve} area${improve > 1 ? 's' : ''} to improve` : 'All within spec'}
        </div>
      </div>
    </div>
  );
};

const GEARS = [
  { g: '1', sev: 'ok' }, { g: '2', sev: 'ok' }, { g: '3', sev: 'attention' },
  { g: '4', sev: 'ok' }, { g: '5', sev: 'ok' }, { g: 'R', sev: 'attention' },
];
const GearShift = ({ t }) => {
  const cur = Math.min(GEARS.length - 1, Math.floor(t * (GEARS.length + 0.5)));
  const smooth = 84;
  return (
    <div className="absolute z-30 left-1/2 -translate-x-1/2 top-[42%] -translate-y-1/2 sm:top-1/2 w-[82%] max-w-lg">
      <div className="rounded-2xl border border-blue-400/40 bg-slate-950/[0.98] backdrop-blur-md px-4 sm:px-5 py-3.5 shadow-2xl shadow-black/60 ring-1 ring-white/5">
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-[0.18em] uppercase text-blue-300"><Gauge className="h-3.5 w-3.5" /> Shift AI · running gears</span>
          <span className="text-[10px] font-mono text-blue-200 tabular-nums">{(t * 5).toFixed(1)}s / 5.0s</span>
        </div>
        <div className="flex items-center justify-center gap-2 mb-3">
          {GEARS.map((gr, i) => {
            const col = GAP_SEV[gr.sev];
            const active = i === cur && t < 0.95;
            const done = t >= 0.95 || i < cur;
            return (
              <div key={gr.g} className="flex flex-col items-center gap-1">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg text-sm font-black tabular-nums transition-all" style={{ background: done || active ? `${col}22` : 'rgba(255,255,255,0.06)', border: `2px solid ${active ? col : done ? `${col}88` : 'rgba(255,255,255,0.15)'}`, color: done || active ? col : '#94a3b8', boxShadow: active ? `0 0 16px ${col}88` : 'none' }}>{gr.g}</div>
                <span className="text-[7px] font-bold uppercase tracking-wide" style={{ color: (done || active) ? col : 'transparent' }}>{gr.sev === 'ok' ? 'smooth' : gr.g === 'R' ? 'jumps' : 'notchy'}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mb-2.5">
          <span className="text-[9px] font-bold uppercase tracking-wide text-slate-300 w-24 shrink-0">Shift smoothness</span>
          <div className="relative flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-amber-400" style={{ width: `${smooth * clamp(t * 1.4, 0, 1)}%` }} />
          </div>
          <span className="w-9 text-right text-[10px] font-mono tabular-nums text-amber-300">{Math.round(smooth * clamp(t * 1.4, 0, 1))}%</span>
        </div>
        <div className="flex items-center justify-center gap-2 border-t border-white/10 pt-2.5">
          <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-slate-400">Gearbox</span>
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-black tracking-wider uppercase" style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b66' }}>
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> ATTENTION
          </span>
          <span className="text-[10px] text-slate-400">· 3rd notchy · slight jump out of R</span>
        </div>
      </div>
    </div>
  );
};

const GapMeasure = ({ gap, appear }) => (
  <motion.div className="absolute z-30 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${gap.x}%`, top: `${gap.y}%` }} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: appear, scale: 1 }} transition={{ duration: 0.3 }}>
    <svg width="86" height="18" aria-hidden>
      <line x1="6" y1="9" x2="80" y2="9" stroke="#ef4444" strokeWidth="2" />
      <path d="M6 9 l8 -5 v10 z" fill="#ef4444" />
      <path d="M80 9 l-8 -5 v10 z" fill="#ef4444" />
    </svg>
    <div className="mt-1 rounded px-1.5 py-0.5 text-[9px] font-black tracking-wider uppercase whitespace-nowrap" style={{ background: '#ef4444', color: '#0b1220' }}>
      Panel gap {gap.value} · {gap.status}
    </div>
  </motion.div>
);

const ENGINE_SOUNDS = [
  { name: 'Knocking', db: 71, threshold: 55, sev: 'major' },
  { name: 'Clicking', db: 60, threshold: 55, sev: 'attention' },
  { name: 'Rumbling', db: 47, threshold: 55, sev: 'ok' },
  { name: 'Grinding', db: 38, threshold: 55, sev: 'ok' },
];

const SoundWave = ({ t }) => {
  const bars = 40;
  const worst = ENGINE_SOUNDS.some((s) => s.sev === 'major') ? 'major' : ENGINE_SOUNDS.some((s) => s.sev === 'attention') ? 'attention' : 'ok';
  const verdict = { major: { c: '#ef4444', t: 'NOT OK · repair advised' }, attention: { c: '#f59e0b', t: 'NEEDS ATTENTION' }, ok: { c: '#10b981', t: 'HEALTHY · OK' } }[worst];
  return (
    <div className="absolute z-30 left-1/2 -translate-x-1/2 top-[40%] -translate-y-1/2 sm:top-1/2 w-[82%] max-w-2xl">
      <div className="rounded-2xl border border-amber-400/40 bg-slate-950/[0.98] backdrop-blur-md px-4 sm:px-5 py-3.5 shadow-2xl shadow-black/60 ring-1 ring-white/5">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-[0.18em] uppercase text-amber-300"><AudioLines className="h-3.5 w-3.5" /> Sound AI · listening</span>
          <span className="text-[10px] font-mono text-amber-200 tabular-nums">{(t * 5).toFixed(1)}s / 5.0s</span>
        </div>

        {/* waveform strip */}
        <div className="flex items-end justify-center gap-[3px] h-9 mb-3">
          {Array.from({ length: bars }).map((_, i) => {
            const played = i / bars <= t;
            const h = 8 + Math.abs(Math.sin(i * 0.7 + t * 22) * 0.6 + Math.sin(i * 0.29 + t * 9) * 0.4) * 30;
            const spike = (i === 14 || i === 26 || i === 33) ? 12 : 0;
            return <span key={i} className="w-[3px] rounded-full" style={{ height: `${h + spike}px`, background: played ? (spike ? '#ef4444' : '#fbbf24') : 'rgba(255,255,255,0.16)' }} />;
          })}
        </div>

        {/* per-sound dB analysis */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          {ENGINE_SOUNDS.map((s, i) => {
            const rowT = clamp((t - 0.12 - i * 0.16) / 0.4, 0, 1);
            const db = Math.round(s.db * easeOut(rowT));
            const over = db > s.threshold;
            const col = s.sev === 'major' ? '#ef4444' : s.sev === 'attention' ? '#f59e0b' : '#10b981';
            const fill = clamp(db / 90, 0, 1) * 100;
            const mark = (s.threshold / 90) * 100;
            return (
              <div key={s.name} className="flex items-center gap-2" style={{ opacity: 0.4 + 0.6 * clamp(rowT * 3, 0, 1) }}>
                <span className="w-16 shrink-0 text-[10px] font-bold text-slate-50">{s.name}</span>
                <div className="relative flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${fill}%`, background: over ? col : '#10b981', transition: 'width 0.1s linear' }} />
                  <span className="absolute top-0 h-full w-px bg-white/50" style={{ left: `${mark}%` }} />
                </div>
                <span className="w-10 shrink-0 text-right text-[10px] font-mono tabular-nums" style={{ color: over ? col : '#10b981' }}>{db}dB</span>
                <span className="w-16 shrink-0 text-[8px] font-black tracking-wide uppercase text-right" style={{ color: over ? col : '#10b981' }}>{rowT > 0.85 ? (over ? 'ELEVATED' : 'IN RANGE') : '···'}</span>
              </div>
            );
          })}
        </div>

        {/* overall engine verdict */}
        <div className="mt-3 flex items-center justify-center gap-2 border-t border-white/10 pt-2.5">
          <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-slate-400">Engine</span>
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-black tracking-wider uppercase" style={{ background: `${verdict.c}22`, color: verdict.c, border: `1px solid ${verdict.c}66` }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: verdict.c }} /> {verdict.t}
          </span>
          <span className="text-[10px] text-slate-400">· timing-chain knock @ 2.1 kHz</span>
        </div>
      </div>
    </div>
  );
};


const BANKS = [
  { name: 'HDFC Bank', short: 'HDFC', sym: 'H', color: '#004C8F', mark: '#ED232A' },
  { name: 'SBI', short: 'SBI', sym: 'S', color: '#22409A', mark: '#1B5FA8' },
  { name: 'ICICI Bank', short: 'ICICI', sym: 'i', color: '#AE2A34', mark: '#F58220' },
  { name: 'Axis Bank', short: 'Axis', sym: 'A', color: '#97144D', mark: '#B4185B' },
  { name: 'Kotak', short: 'Kotak', sym: 'K', color: '#003874', mark: '#EF3E42' },
];

const BankTile = ({ bank }) => (
  <div className="flex items-center gap-2 rounded-lg bg-white px-2 py-1.5 shadow-md ring-1 ring-black/5">
    <span className="flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-md text-[11px] sm:text-sm font-black leading-none text-white" style={{ background: `linear-gradient(135deg, ${bank.color}, ${bank.mark})` }}>{bank.sym}</span>
    <span className="truncate text-[10px] sm:text-[11px] font-black leading-none tracking-tight" style={{ color: bank.color }}>{bank.name}</span>
  </div>
);

const VerdictCard = ({ reachedCount }) => {
  const score = 6.8; const pct = score / 10;
  const R = 44; const C = 2 * Math.PI * R;
  const topRepairs = [...COST_ITEMS].sort((a, b) => b.cost - a.cost);
  return (
    <motion.div className="absolute inset-0 z-40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <div className="absolute inset-0 bg-slate-950" />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(55% 60% at 15% 0%, rgba(37,99,235,0.16), transparent 60%), radial-gradient(50% 55% at 100% 100%, rgba(16,185,129,0.12), transparent 60%)' }} />
      <div className="relative h-full w-full flex flex-col p-4 sm:p-6 lg:px-8 lg:py-6">
        {/* header */}
        <div className="flex items-center gap-3 sm:gap-5 shrink-0">
          <div className="relative shrink-0 h-[64px] w-[64px] sm:h-[100px] sm:w-[100px]">
            <svg viewBox="0 0 100 100" className="-rotate-90 h-full w-full">
              <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="9" />
              <motion.circle cx="50" cy="50" r={R} fill="none" stroke="#f59e0b" strokeWidth="9" strokeLinecap="round" strokeDasharray={C} initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C * (1 - pct) }} transition={{ duration: 1.1, ease: 'easeOut' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl sm:text-3xl font-black tabular-nums text-white leading-none">{score}</span>
              <span className="text-[8px] sm:text-[9px] font-bold tracking-widest uppercase text-slate-400 mt-0.5">/ 10</span>
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-black tracking-[0.16em] uppercase text-amber-300">
                <ShieldCheck className="h-3.5 w-3.5" /> Buy with caution
              </span>
              <span className="text-[10px] font-bold tracking-[0.12em] uppercase text-slate-400">Wisedrive verdict · Hyundai Creta</span>
            </div>
            <h3 className="mt-1 sm:mt-1.5 text-base sm:text-2xl lg:text-3xl font-black text-white leading-tight">Negotiate {inr(GRAND_TOTAL)} — then drive worry-free.</h3>
            <p className="mt-1 text-xs sm:text-sm text-slate-300 hidden sm:block">{reachedCount} findings across 200+ checkpoints — every one fixable, and priced with photo + AI evidence.</p>
          </div>
        </div>

        {/* four blocks */}
        <div className="mt-3 sm:mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 flex-1 min-h-0 auto-rows-fr">
          {/* 1 · recommendation */}
          <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/[0.07] p-3 sm:p-3.5 flex flex-col overflow-hidden">
            <div className="flex items-center gap-1.5 text-[10px] font-black tracking-[0.14em] uppercase text-emerald-300"><ThumbsUp className="h-3.5 w-3.5" /> Recommendation</div>
            <ul className="hidden sm:block mt-2.5 space-y-1.5 text-[11px] text-slate-300">
              {['No chassis / structural damage', 'Genuine odometer · 48,200 km', 'Clean title & insurance'].map((x) => (
                <li key={x} className="flex items-start gap-1.5"><span className="text-emerald-400 font-black leading-none">✓</span><span className="leading-snug">{x}</span></li>
              ))}
            </ul>
            <div className="mt-auto pt-2">
              <div className="text-base sm:text-xl font-black text-emerald-200 leading-tight">Negotiate, then buy</div>
              <p className="mt-1 text-[11px] sm:text-xs text-slate-300 leading-snug"><span className="sm:hidden">Fix the flagged items and it&apos;s a confident buy.</span><span className="hidden sm:inline">Good bones and honest history. Settle the flagged repairs into the price and it&apos;s a confident buy.</span></p>
            </div>
          </div>

          {/* 2 · estimated repairs */}
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 sm:p-3.5 flex flex-col overflow-hidden">
            <div className="flex items-center gap-1.5 text-[10px] font-black tracking-[0.14em] uppercase text-slate-200"><Wrench className="h-3.5 w-3.5 text-amber-300" /> Estimated repairs</div>
            <ul className="hidden sm:block mt-2 space-y-1 sm:flex-1 sm:min-h-0 overflow-hidden">
              {topRepairs.slice(0, 4).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="flex items-center gap-1.5 min-w-0"><span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: SEV[r.severity].c }} /><span className="truncate text-slate-300">{r.label}</span></span>
                  <span className="font-mono tabular-nums text-slate-200 shrink-0">{inr(r.cost)}</span>
                </li>
              ))}
              <li className="text-[10px] text-slate-500">+ {topRepairs.length - 4} more findings</li>
            </ul>
            {/* mobile compact */}
            <div className="sm:hidden mt-auto pt-1">
              <div className="text-xl font-black tabular-nums text-amber-300 leading-none">{inr(GRAND_TOTAL)}</div>
              <div className="mt-1 text-[10px] text-slate-400">across {reachedCount} findings</div>
            </div>
            {/* desktop total */}
            <div className="hidden sm:flex mt-1.5 border-t border-white/10 pt-2 items-baseline justify-between">
              <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Total</span>
              <span className="text-lg font-black tabular-nums text-amber-300">{inr(GRAND_TOTAL)}</span>
            </div>
          </div>

          {/* 3 · extended warranty */}
          <div className="rounded-xl border border-blue-400/25 bg-blue-500/[0.07] p-3 sm:p-3.5 flex flex-col overflow-hidden">
            <div className="flex items-center gap-1.5 text-[10px] font-black tracking-[0.14em] uppercase text-blue-200"><ShieldCheck className="h-3.5 w-3.5" /> Extended warranty</div>
            <div className="mt-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-2 py-0.5 text-[9px] font-black tracking-wider uppercase text-emerald-300">✓ Eligible</span>
              <div className="mt-2 text-base sm:text-2xl font-black text-white leading-none">12–24 <span className="text-sm font-bold text-slate-300">months</span></div>
              <p className="mt-1.5 text-[11px] sm:text-xs text-slate-300 leading-snug"><span className="sm:hidden">Covers <strong className="text-white">engine</strong> &amp; gearbox.</span><span className="hidden sm:inline">Covers <strong className="text-white">engine</strong>, gearbox &amp; core electricals — activated after Wisedrive repairs.</span></p>
            </div>
          </div>

          {/* 4 · financing */}
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 sm:p-3.5 flex flex-col overflow-hidden">
            <div className="flex items-center gap-1.5 text-[10px] font-black tracking-[0.14em] uppercase text-slate-200"><Landmark className="h-3.5 w-3.5 text-blue-300" /> Financing</div>
            <div className="mt-2">
              <div className="text-sm sm:text-xl font-black text-white leading-tight">Up to 90% funding</div>
              <div className="text-[10px] sm:text-[11px] text-slate-300">from <strong className="text-emerald-300">10.45% p.a.</strong><span className="hidden sm:inline"> · top 5 banks</span></div>
            </div>
            {/* mobile: compact bank chips that fit the small cell (no clipping / truncation) */}
            <div className="sm:hidden mt-2 flex flex-wrap gap-1">
              {BANKS.map((b) => (
                <span key={b.name} className="inline-flex items-center gap-1 rounded bg-white px-1.5 py-0.5">
                  <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: b.color }} />
                  <span className="text-[9px] font-black leading-none" style={{ color: b.color }}>{b.short}</span>
                </span>
              ))}
            </div>
            {/* desktop: full logo tiles */}
            <div className="hidden sm:grid mt-2.5 grid-cols-2 gap-2 flex-1 content-start">
              {BANKS.map((b) => <BankTile key={b.name} bank={b} />)}
              <span className="col-span-2 mt-0.5 inline-flex items-center gap-1.5 text-[10px] text-slate-400">
                <ShieldCheck className="h-3 w-3 text-emerald-400" /> Pre-approved offer in ~2 min
              </span>
            </div>
          </div>
        </div>

        {/* CTA strip */}
        <div className="mt-3 sm:mt-4 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 shrink-0">
          <span className="text-[11px] sm:text-sm text-slate-300">Full report, warranty &amp; pre-approved loan offer — sent to your WhatsApp.</span>
          <a
            href={`https://wa.me/917338073380?text=${encodeURIComponent('Hi Wisedrive! I just watched the inspection film — please send me the full report, extended warranty & financing offer for this car.')}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="inspection-3d-get-report"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-3.5 py-1.5 text-xs font-black whitespace-nowrap transition-colors"
          >Get my report <ArrowRight className="h-3.5 w-3.5" /></a>
        </div>
      </div>
    </motion.div>
  );
};

const Inspection3D = () => {
  const [elapsed, setElapsed] = useState(0);
  const [inView, setInView] = useState(false);
  const [userSpin, setUserSpin] = useState(null);
  const [spinFrame, setSpinFrame] = useState(0);
  const sectionRef = useRef(null);
  const dragRef = useRef({ dragging: false, startX: 0, startFrame: 0 });
  const stateRef = useRef({ inView: false, userSpin: null });

  useEffect(() => { stateRef.current.inView = inView; }, [inView]);
  useEffect(() => { stateRef.current.userSpin = userSpin; }, [userSpin]);

  // preload frames + stills
  useEffect(() => {
    const urls = [
      ...Array.from({ length: FRAME_COUNT }, (_, i) => FRAME_URL(i)),
      ...CH.filter((c) => c.type === 'image').map((c) => c.src),
    ];
    urls.forEach((u) => { const img = new Image(); img.src = u; img.decode?.().catch(() => {}); });
  }, []);

  useEffect(() => {
    if (!sectionRef.current) return undefined;
    const io = new IntersectionObserver(
      (e) => e.forEach((x) => setInView(x.isIntersecting)),
      { threshold: 0.1, rootMargin: '0px 0px -80px 0px' },
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  // single ref-guarded clock — cannot double (StrictMode-safe)
  useEffect(() => {
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (stateRef.current.inView && stateRef.current.userSpin === null) {
        setElapsed((e) => (e + dt) % TOTAL);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const activeIndex = useMemo(() => {
    let idx = 0;
    for (let i = 0; i < CH.length; i += 1) if (elapsed >= CH[i].start) idx = i;
    return idx;
  }, [elapsed]);
  const active = CH[activeIndex];
  const sev = SEV[active.severity];
  const localT = clamp((elapsed - active.start) / active.dur, 0, 1);

  // turntable frame: spin during exterior, lock for accident/dents
  const ext = CH[1];
  const spinFromClock = useMemo(() => clamp((elapsed - ext.start) / ext.dur, 0, 1) * FRAME_COUNT, [elapsed, ext.start, ext.dur]);
  useEffect(() => {
    if (userSpin !== null) { setSpinFrame(userSpin); return; }
    if (active.type === 'turntable' && active.spin) setSpinFrame(spinFromClock);
    else if (active.type === 'turntable') setSpinFrame(active.frame);
  }, [spinFromClock, userSpin, active]);
  const frameIndex = Math.round(((spinFrame % FRAME_COUNT) + FRAME_COUNT) % FRAME_COUNT);

  const reached = useMemo(() => COST_ITEMS.filter((c) => elapsed >= c.start), [elapsed]);
  const runningTotal = reached.reduce((s, c) => s + c.cost, 0);

  const metricValue = () => {
    const m = active.metric;
    if (!m) return null;
    if (m.text) return m.text;
    const v = m.from + (m.to - m.from) * easeOut(localT);
    return (m.dp != null ? v.toFixed(m.dp) : Math.round(v)) + (m.unit || '');
  };

  const canDrag = active.type === 'turntable' && active.spin;
  const onPointerDown = (e) => {
    if (!canDrag) return;
    dragRef.current = { dragging: true, startX: e.clientX, startFrame: spinFrame };
    setUserSpin(spinFrame);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    setUserSpin(dragRef.current.startFrame - (e.clientX - dragRef.current.startX) / 18);
  };
  const onPointerUp = () => { dragRef.current.dragging = false; };
  const resumeTour = () => setUserSpin(null);

  const AiIcon = { MOBILE: Smartphone, VISION: ScanLine, SOUND: AudioLines, SENSOR: Gauge, VERDICT: ShieldCheck }[active.ai];

  return (
    <section
      ref={sectionRef}
      data-testid="inspection-3d-section"
      className="relative bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white overflow-hidden"
      aria-label="Wisedrive cinematic car inspection film"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(70% 50% at 50% 0%, rgba(37,99,235,0.16) 0%, rgba(2,6,23,0) 60%), radial-gradient(50% 40% at 88% 100%, rgba(16,185,129,0.10) 0%, rgba(2,6,23,0) 65%)' }} />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="max-w-3xl mb-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/10 px-3 py-1 text-xs font-bold tracking-[0.16em] uppercase text-blue-300">
            <Smartphone className="h-3.5 w-3.5" /> Filmed on the Wisedrive app
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
            Watch a real inspection. Vision AI, Sound AI, verdict.
          </h2>
          <p className="mt-4 text-sm sm:text-lg text-slate-300">
            200+ checkpoints, filmed on a phone at the seller&apos;s doorstep — then every finding is priced,
            live, so you know exactly what to negotiate.
          </p>
        </div>

        {/* ── Full-frame film ── */}
        <div
          data-testid="inspection-3d-canvas"
          className="relative w-full rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-black/60 bg-slate-900 select-none aspect-[3/4] sm:aspect-video"
          style={{ touchAction: 'pan-y', cursor: canDrag ? 'grab' : 'default' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* media layers */}
          {CH.map((c, i) => {
            if (c.type !== 'image' && c.type !== 'turntable') return null;
            const fadeIn = clamp((elapsed - (c.start - CROSS)) / CROSS, 0, 1);
            const fadeOut = clamp((c.end - elapsed) / CROSS, 0, 1);
            const presence = Math.min(fadeIn, fadeOut);
            if (!(presence > 0.01)) return null;
            const lp = clamp((elapsed - c.start) / c.dur, 0, 1);
            const isVision = !!c.vision;
            const bx = c.boxes && c.boxes[0] ? c.boxes[0].x + c.boxes[0].w / 2 : 50;
            const by = c.boxes && c.boxes[0] ? c.boxes[0].y + c.boxes[0].h / 2 : 50;
            const scale = isVision ? 1.04 + 0.3 * lp : 1.05 + 0.12 * lp;
            const panX = isVision ? 0 : (i % 2 === 0 ? 1 : -1) * 12 * lp;
            const panY = isVision ? 0 : -8 * lp;
            const origin = isVision ? `${bx}% ${by}%` : 'center';
            return (
              <div key={c.id} className="absolute inset-0" style={{ opacity: presence, zIndex: i, background: '#0b1220' }}>
                {c.type === 'turntable' ? (
                  <img src={FRAME_URL(frameIndex)} alt={`Hyundai Creta 360 frame ${frameIndex + 1}`} data-testid="inspection-3d-frame" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} decoding="sync" />
                ) : (
                  <>
                    <img src={c.src} alt={c.label} className="absolute inset-0 w-full h-full object-cover pointer-events-none will-change-transform" draggable={false} style={{ transform: `scale(${scale}) translate3d(${panX}px,${panY}px,0)`, transformOrigin: origin, transition: 'transform 0.12s linear' }} />
                    {c.src2 && (
                      <img src={c.src2} alt={`${c.label} rear`} className="absolute inset-0 w-full h-full object-cover pointer-events-none will-change-transform" draggable={false} style={{ transform: `scale(${1.05 + 0.12 * lp}) translate3d(${-panX}px,${panY}px,0)`, transformOrigin: 'center', opacity: clamp((lp - 0.42) / 0.24, 0, 1), transition: 'opacity 0.25s linear, transform 0.12s linear' }} />
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* AI overlays for the active beat */}
          {active.type !== 'verdict' && (active.ai === 'VISION' || active.ai === 'MOBILE') && <ScanSweep t={localT} color={sev.c} />}
          {(active.ai === 'MOBILE' || (active.ai === 'VISION' && active.spin)) && <PhoneHud />}
          {active.boxes && active.boxes.map((b, i) => (
            <VisionBox key={i} box={b} appear={clamp((localT - 0.12) * 4, 0, 1)} />
          ))}
          {active.gap && <GapMeasure gap={active.gap} appear={clamp((localT - 0.35) * 4, 0, 1)} />}
          {active.ai === 'SOUND' && <SoundWave t={localT} />}
          {active.gear && <GearShift t={localT} />}
          {active.checks && <CheckPanel title={active.label} items={active.checks} t={localT} />}

          {/* film grain + vignette + scrims */}
          <div className="pointer-events-none absolute inset-0 z-20" style={{ background: 'radial-gradient(120% 100% at 50% 45%, rgba(0,0,0,0) 55%, rgba(2,6,23,0.42) 100%)' }} />
          <div className="pointer-events-none absolute inset-0 z-20 mix-blend-overlay opacity-[0.10]" style={{ backgroundImage: 'repeating-linear-gradient(to bottom, rgba(255,255,255,0.9) 0 1px, transparent 1px 3px)' }} />
          <div className="pointer-events-none absolute inset-0 z-20 mix-blend-overlay opacity-[0.06] animate-[grainShift_1.1s_steps(3)_infinite]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")", backgroundSize: '160px 160px' }} />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 z-20" style={{ background: 'linear-gradient(to bottom, rgba(2,6,23,0.6), rgba(2,6,23,0))' }} />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-44 z-20" style={{ background: 'linear-gradient(to top, rgba(2,6,23,0.78), rgba(2,6,23,0))' }} />

          {/* verdict */}
          {active.type === 'verdict' && <VerdictCard reachedCount={COST_ITEMS.length} />}

          {/* records & documents */}
          {active.type === 'records' && <RecordsBeat t={localT} kicker={active.kicker} total={BEAT_COUNT} />}

          {/* obd-ii diagnostic */}
          {active.type === 'obd' && <OBDBeat t={localT} kicker={active.kicker} total={BEAT_COUNT} />}

          {/* ── HUD ── */}
          {active.type !== 'verdict' && active.type !== 'records' && active.type !== 'obd' && (
            <>
              {/* top-left: AI mode */}
              <div className="z-40 absolute top-3 left-3 sm:top-4 sm:left-4 flex flex-wrap items-center gap-1.5 sm:gap-2 max-w-[62%] sm:max-w-none">
                <span className="inline-flex items-center gap-1.5 rounded-full backdrop-blur-md px-2.5 py-1 text-[10px] font-black tracking-[0.16em] uppercase" style={{ background: `${sev.c}1f`, border: `1px solid ${sev.c}66`, color: sev.c }}>
                  {AiIcon && <AiIcon className="h-3 w-3" />} {active.ai} AI
                </span>
              </div>

              {/* top-right: estimate + mobile chip (estimate lives inside the frame) */}
              <div className="z-40 absolute top-3 right-3 sm:top-4 sm:right-4 flex flex-col items-end gap-2">
                <div data-testid="inspection-3d-inline-total" className="rounded-xl px-2.5 py-1 sm:px-3.5 sm:py-2 border backdrop-blur-md text-right" style={{ background: 'rgba(2,6,23,0.6)', borderColor: 'rgba(59,130,246,0.5)' }}>
                  <div className="text-[8px] sm:text-[9px] font-bold tracking-[0.18em] uppercase text-blue-300">Estimate so far</div>
                  <motion.div key={runningTotal} initial={{ opacity: 0.4, y: -3 }} animate={{ opacity: 1, y: 0 }} data-testid="inspection-3d-estimate-total" className="text-lg sm:text-2xl font-black tabular-nums text-white leading-tight">{inr(runningTotal)}</motion.div>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15 px-2.5 py-1 text-[10px] font-semibold text-slate-200">
                  <Smartphone className="h-3 w-3 text-emerald-400" /> Shot on mobile
                </span>
              </div>

              {/* bottom-left: title card */}
              <div className="z-40 absolute bottom-9 left-4 right-4 flex items-end justify-between gap-3">
                <AnimatePresence mode="wait">
                  <motion.div key={active.id} data-testid="inspection-3d-title-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35, ease: 'easeOut' }} className="min-w-0">
                    <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[9px] font-black tracking-[0.16em] uppercase" style={{ background: `${sev.c}26`, color: sev.c, border: `1px solid ${sev.c}66` }}>{sev.t}</span>
                    <div className="mt-1.5 text-2xl sm:text-3xl font-black tracking-tight text-white" style={{ textShadow: '0 2px 18px rgba(0,0,0,0.7)' }}>{active.label}</div>
                    <div className="mt-0.5 text-[11px] sm:text-xs text-slate-300 max-w-md leading-snug">{active.sub}</div>
                    {active.note && (
                      <div className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] sm:text-[11px] font-bold" style={{ background: `${sev.c}1f`, color: sev.c, border: `1px solid ${sev.c}55` }}>
                        <Gauge className="h-3 w-3 shrink-0" /> {active.note}
                      </div>
                    )}
                    {active.cost > 0 && (
                      <div data-testid="inspection-3d-cost-badge" className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-black tabular-nums" style={{ background: `${sev.c}22`, color: sev.c, border: `1px solid ${sev.c}66` }}>
                        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: sev.c }} />
                        {active.lineItem} · {inr(active.cost)}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* animated measurement readout — top-center (desktop only) */}
              {active.metric && (
                <motion.div key={`m-${active.id}`} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid="inspection-3d-metric" className="z-40 absolute top-14 left-1/2 -translate-x-1/2 hidden sm:inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 border backdrop-blur-md" style={{ background: 'rgba(2,6,23,0.6)', borderColor: `${sev.c}55` }}>
                  <span className="text-[9px] font-bold tracking-[0.18em] uppercase text-slate-300">{active.metric.label}</span>
                  <span className="text-base sm:text-lg font-black tabular-nums leading-none" style={{ color: sev.c, fontFamily: 'ui-monospace,monospace' }}>{metricValue()}</span>
                </motion.div>
              )}
            </>
          )}

          {/* floating resume pill (only while user is spinning the 360) */}
          {userSpin !== null && (
            <button type="button" onClick={resumeTour} onPointerDown={(e) => e.stopPropagation()} data-testid="inspection-3d-resume-tour" className="z-40 absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-blue-500/25 hover:bg-blue-500/40 border border-blue-400/50 px-3 py-1.5 text-[11px] font-semibold text-blue-100 transition-colors">
              <RotateCw className="h-3 w-3" /> Resume film
            </button>
          )}
        </div>

        {/* CTA-ish caption under the film */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-slate-400 text-sm">
          <span>Total negotiable across all findings: <strong className="text-white tabular-nums">{inr(GRAND_TOTAL)}</strong></span>
          <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Every rupee backed by photo + AI evidence</span>
        </div>
      </div>

      <style>{`
        @keyframes grainShift {
          0% { transform: translate(0,0); }
          33% { transform: translate(-6px,4px); }
          66% { transform: translate(5px,-5px); }
          100% { transform: translate(0,0); }
        }
      `}</style>
    </section>
  );
};

export default Inspection3D;
